import { use, ElementType, createContext } from 'react'
import { system } from '@workday/canvas-tokens-web'
import { ItemsListItems } from '~/components/v2/ItemsList'
import { ResultItems, ResultItemsSectionHeader } from '~/components/v2/ResultsLayout/ResultItem'
import {
  Box,
  BoxProps,
  Flex,
  FlexProps,
  Heading,
  HeadingProps,
  Stack,
  StackProps,
  RegionErrorBoundary
} from '~/design_system'
import { $thm } from '~/common/themeSx'
import { useIsSanaRebrandEnabled } from '~/theme/useIsSanaRebrandEnabled'

/**
 * Context to indicate that ResultsLayout is being used inside ListLayout.
 * This allows ResultsLayout to detect when it's being used directly vs via ListLayout.
 */
export const ListLayoutInternalContext = createContext(false)

export * from './ActionBar/ActionBar'
export * from './ActionBar/AutoActionBar'

/**
 * @module ResultsLayout
 *
 * This module contains the `ResultsLayout` template. The purpose of this
 * template is to provide an easy way to render a page with a main content area, a sidebar and a top bar
 *
 * @see `ResultsLayout.stories.jsx` for usage examples.
 */

/**
 * The main container for `ResultsLayout`. Use the `ResultsLayoutActionBar` and `ResultsLayoutMainContent` components
 * as the direct children for `ResultsLayoutMain`.
 *
 * @example
 *
 * <ResultsLayout.Main>
 *   <ResultsLayout.ActionBar>I'm the header!</ResultsLayout.ActionBar>
 *   <ResultsLayout.MainContent>I'm the content!</ResultsLayout.MainContent>
 * </ResultsLayout.Main>
 *
 * The underlying component is Chakra's `Stack` component.
 */
const ResultsLayoutMain = (props: StackProps) => (
  <Stack data-results-layout-main flex="1" minW={0} spacing={6} ml={{ base: 0, lg: 6 }} {...props} />
)
ResultsLayoutMain.displayName = 'ResultsLayout.Main'

/**
 * Like ResultsLayout.Main but with no gap between children.
 *
 * Legacy: action + bulk + list are one stack (`spacing="-1px"`).
 * Rebrand: tonal ActionBar is its own card. White BulkActionBar attaches to the shared white list card.
 * Grid: no shared list card. Bulk is its own card above the item cards.
 */
const ResultsLayoutMainNoSpacing = (props: StackProps) => {
  const isRebrand = useIsSanaRebrandEnabled()
  const listCardBorder = `1px solid var(${system.color.border.strong})`
  const bulkListSeam = `1px solid var(${system.color.border.default})`
  const listCardRadius = 'var(--chakra-radii-xxl)'

  const legacySx = {
    '[data-results-layout-main-content]': {
      marginBottom: 6,
      '&:has([data-result-items])': {
        border: '1px',
        borderColor: 'gray.300',
        roundedBottom: 'base',
        overflow: 'hidden'
      },
      '[data-empty-state-container]': {
        bg: 'white',
        border: '1px',
        borderColor: 'gray.300',
        roundedBottom: 'base',
        overflow: 'hidden'
      }
    },
    /* SplitList already wraps the pane in a border; skip the list empty-state chrome to avoid double boxes. */
    '[data-results-layout-main-content]:has([data-split-list]) [data-empty-state-container]': {
      bg: 'transparent',
      border: 'none',
      boxShadow: 'none',
      borderRadius: 0,
      overflow: 'visible'
    },
    '[data-result-items]': {
      border: 'none',
      rounded: 'none',
      boxShadow: 'none',
      '--vndly-legacy-items-list-edge-radius': '0',
      '--vndly-items-list-start-radius': '0',
      '--vndly-items-list-end-radius': '0'
    }
  }

  const rebrandSx = {
    '[data-action-bar]': {
      borderRadius: 'xxl',
      marginBottom: 4
    },
    // `[data-canvas-theme] &` beats BulkActionBar's `$canvas: border none`.
    // Without the ancestor attr, `[data-canvas-theme] .css-x` and
    // `.parent [data-bulk-action-bar]` tie and `border: none` wins.
    '[data-canvas-theme] & [data-bulk-action-bar]': {
      bg: `var(${system.color.bg.default})`,
      border: listCardBorder,
      borderBottom: bulkListSeam,
      borderTopRadius: 'xxl',
      borderBottomRadius: 0,
      boxShadow: 'none',
      marginBottom: 0
    },
    // Only the MainNoSpacing → MainContent direct child is the shared list card.
    // Nested MainContent (worker-provisioning) must not get a second outline.
    '& > [data-results-layout-main-content]': {
      marginBottom: 6,
      '&:has([data-result-items]), &:has([data-empty-state-container]), &:has([data-split-list])': {
        bg: `var(${system.color.bg.default})`,
        border: listCardBorder,
        borderTopRadius: 'xxl',
        borderBottomRadius: 'xxl',
        '--vndly-legacy-items-list-edge-radius': '0',
        // First/last rows paint the shell's corners. No overflow clip.
        '--vndly-items-list-start-radius': listCardRadius,
        '--vndly-items-list-end-radius': listCardRadius,
        boxShadow: 'none'
      },
      '[data-empty-state-container]': {
        bg: 'transparent',
        border: 'none',
        boxShadow: 'none'
      },
      // SplitList draws its own border for standalone use. Inside this shell,
      // strip that border so we don't stack two boxes.
      '[data-split-list]': {
        border: 'none',
        boxShadow: 'none'
      }
    },
    // Bulk caps the list card, so drop the top edge on the rows below.
    // NProgress is inset for xxl corners. A square top takes the full width.
    '[data-canvas-theme] &:not(:has([data-items-list-display-mode="grid"])) [data-bulk-action-bar] + [data-results-layout-main-content]':
      {
        borderTop: 'none',
        borderTopRadius: 0,
        '--vndly-items-list-start-radius': '0',
        '[data-nprogress-bar-container]': {
          left: 0,
          right: 0
        }
      },
    /* Shared shell already borders the SplitList pane. Skip the detail empty-state box. */
    '[data-results-layout-main-content]:has([data-split-list]) [data-empty-state-container]': {
      bg: 'transparent',
      border: 'none',
      boxShadow: 'none',
      borderRadius: 0,
      overflow: 'visible'
    },
    '[data-canvas-theme] & [data-result-items]': {
      border: 'none',
      boxShadow: 'none',
      bg: 'transparent',
      borderTopRadius: 'var(--vndly-items-list-start-radius, 0)',
      borderBottomRadius: 'var(--vndly-items-list-end-radius, 0)'
    },
    // Flattened so stylis does not emit `[data-canvas-theme] .parent` inside `.parent:has(...)`.
    '[data-canvas-theme] &:has([data-items-list-display-mode="grid"]) [data-bulk-action-bar]': {
      borderBottom: listCardBorder,
      borderRadius: 'xxl',
      marginBottom: 4
    },
    '&:has([data-items-list-display-mode="grid"])': {
      '& > [data-results-layout-main-content]': {
        bg: 'transparent',
        border: 'none',
        boxShadow: 'none',
        borderRadius: 0,
        overflow: 'visible',
        marginTop: 0,
        // Grid cards own their xxl radius. Don't inherit list-shell square-row vars.
        '--vndly-legacy-items-list-edge-radius': 'unset',
        '--vndly-items-list-start-radius': 'unset',
        '--vndly-items-list-end-radius': 'unset'
      }
    }
  }

  return (
    <ResultsLayoutMain
      data--no-spacing
      spacing={isRebrand ? 0 : '-1px'}
      sx={isRebrand ? rebrandSx : legacySx}
      {...props}
    />
  )
}
ResultsLayoutMainNoSpacing.displayName = 'ResultsLayout.MainNoSpacing'

/**
 * The main content section for the view. Since pages have many different ways
 * to lay out their content, this is simply an empty container. You will either
 * need to style it directly or provide your own styled container as
 * `children`.
 *
 * The underlying component is Chakra's `Box` component.
 */
const ResultsLayoutMainContent = ({ children, ...props }: BoxProps) => {
  return (
    <Box position="relative" data-results-layout-main-content {...props}>
      <NProgressContainer />
      <RegionErrorBoundary>{children}</RegionErrorBoundary>
    </Box>
  )
}
ResultsLayoutMainContent.displayName = 'ResultsLayout.MainContent'

// This is where the NProgress bar will be rendered by the nprogress library
function NProgressContainer() {
  return (
    <Box
      data-nprogress-bar-container
      overflow="hidden"
      h="5px"
      // @ts-ignore
      position="absolute !important"
      top={0}
      left={6}
      right={6}
    />
  )
}

/**
 * The container for the sidebar portion of `ResultsLayout`. Use the
 * `ResultsLayoutSidebarHeader` and `ResultsLayoutSidebarContent` components as the children for
 * `ResultsLayoutSidebar`.
 *
 * @example
 *
 * <ResultsLayout.Sidebar>
 *   <ResultsLayout.SidebarHeader>Header!</ResultsLayout.SidebarHeader>
 *   <ResultsLayout.SidebarContent>Content!</ResultsLayout.SidebarContent>
 * </ResultsLayout.Sidebar>
 *
 * The underlying component is Chakra's `Flex`.
 */
const ResultsLayoutSidebar = (props: FlexProps) => {
  const isRebrand = useIsSanaRebrandEnabled()
  return (
    <Flex
      {...(isRebrand
        ? {
            layerStyle: 'cardTonal',
            // Clip checkbox lists / accordion panels to the xxl radius.
            overflow: 'hidden',
            // The tonal card fits its filters instead of stretching to the results' height.
            alignSelf: 'flex-start'
          }
        : { bg: 'gray.50', borderRadius: 'base', border: '1px', borderColor: 'gray.300' })}
      flexDirection="column"
      width="240px"
      display={{ base: 'none', lg: 'flex' }}
      flexShrink={0}
      {...props}
      data-testid="results-layout-sidebar"
    />
  )
}
ResultsLayoutSidebar.displayName = 'ResultsLayout.Sidebar'

/**
 * Renders a styled header for the sidebar.
 *
 * The header takes an optional `icon` prop that should be an icon imported
 * from `react-icons`.
 *
 * @example
 *
 * import { MdFilterList } from 'react-icons/md'
 *
 * <ResultsLayout.SidebarHeader icon={MdFilterList}>Filters</ResultsLayout.SidebarHeader>
 *
 * The underlying component is Chakra's `Stack` component.
 */
const ResultsLayoutSidebarHeader = ({ children, icon, sx, ...props }: SidebarHeaderProps) => (
  <Stack
    isInline
    p={3}
    alignItems="center"
    sx={$thm({
      borderBottom: { $base: '1px', $canvas: 0 },
      borderColor: 'var(--vndly-color-container-border)',
      ...sx
    })}
    {...props}>
    {icon && <Box as={icon} color="--vndly-color-container-icon" textStyle="heading2" />}
    {children}
  </Stack>
)
type SidebarHeaderProps = { icon?: ElementType } & StackProps
ResultsLayoutSidebarHeader.displayName = 'ResultsLayout.SidebarHeader'

const ResultsLayoutSidebarHeading = (props: HeadingProps) => (
  <Heading flexGrow={1} textStyle="subtextSemi" letterSpacing="1px" {...props} />
)
ResultsLayoutSidebarHeading.displayName = 'ResultsLayout.SidebarHeading'

/**
 * Renders the content area for the sidebar.
 *
 * The underlying component is Chakra's `Stack` component.
 */
const ResultsLayoutSidebarContent = (props: StackProps) => <Stack {...props} />
ResultsLayoutSidebarContent.displayName = 'ResultsLayout.SidebarContent'

/**
 * Renders a simple footer with top border
 *
 * The underlying component is Chakra's `Stack` component.
 */
const ResultsLayoutSidebarFooter = (props: StackProps) => (
  <Stack
    p={3}
    alignItems="center"
    borderTop="1px"
    borderColor="var(--vndly-color-container-border)"
    mt="-1px"
    {...props}
  />
)
ResultsLayoutSidebarFooter.displayName = 'ResultsLayout.SidebarFooter'

/**
 * Renders a list of items in the sidebar
 *
 * The underlying component is Chakra's `Stack` component.
 */
const ResultsLayoutItems = ({ children, ...props }: StackProps) => (
  <ResultItems {...props}>
    <ItemsListItems>{children}</ItemsListItems>
  </ResultItems>
)

ResultsLayoutItems.displayName = 'ResultsLayout.Items'

interface ResultsLayoutProps extends FlexProps {
  /**
   * @deprecated This prop exists only for grandfathered pages that predate ListLayout. New code should use ListLayout instead.
   */
  allowDeprecatedUseOutsideOfListLayout?: boolean
}

/**
 * The main container for the sidebar view, which renders a sidebar to the left
 * of the main content area. Use the other components from this module as the
 * direct children for `ResultsLayout`.
 *
 * **Note:** For list pages, prefer using `ListLayout` which handles providers,
 * loading states, and pagination automatically.
 *
 * The underlying component is Chakra's `Flex`.
 *
 * @see `ListLayout` for the higher-level abstraction.
 * @see `ResultsLayout.stories.jsx` for usage examples.
 */
const ResultsLayoutRoot = ({ allowDeprecatedUseOutsideOfListLayout, sx, ...props }: ResultsLayoutProps) => {
  const isInsideListLayout = use(ListLayoutInternalContext)

  if (process.env.NODE_ENV === 'development' && !isInsideListLayout && !allowDeprecatedUseOutsideOfListLayout) {
    throw new Error(
      'ResultsLayout is being used outside of ListLayout.\n\n' +
        'Prefer using ListLayout which handles providers, loading states, and pagination automatically.\n\n' +
        'If you MUST use ResultsLayout directly, add the `allowDeprecatedUseOutsideOfListLayout` prop:\n' +
        '  <ResultsLayout allowDeprecatedUseOutsideOfListLayout>\n\n' +
        'See the ListLayout documentation for more information.'
    )
  }

  return <Flex sx={{ ...$thm({ my: { $base: 5, $canvas: 0 } }), ...sx }} {...props} />
}

export const ResultsLayout = Object.assign(ResultsLayoutRoot, {
  Main: ResultsLayoutMain,
  MainNoSpacing: ResultsLayoutMainNoSpacing,
  MainContent: ResultsLayoutMainContent,
  Sidebar: ResultsLayoutSidebar,
  SidebarHeader: ResultsLayoutSidebarHeader,
  SidebarHeading: ResultsLayoutSidebarHeading,
  SidebarContent: ResultsLayoutSidebarContent,
  SidebarFooter: ResultsLayoutSidebarFooter,
  Items: ResultsLayoutItems,
  ItemsSectionHeader: ResultItemsSectionHeader
})
